import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { isMobile } from '@/utils';
import Styles from './index.module.scss';

export interface ICommonInput {
  id?: string;
  className?: string;
  maxLength?: number;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  enableClear?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  isNoInput?: boolean,
  errorMsg?: string;
  onChange?: (val?: string) => void;
  onFocus?: (val?: string) => void;
  onBlur?: (val?: string) => void;
  onDelete?: (val?: string) => void;
}

const CommonInput = ({
  className = '',
  defaultValue = '',
  value = '',
  disabled = false,
  enableClear = false,
  isNoInput = false,
  errorMsg = '',
  maxLength = 10000,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onDelete = () => {},
  ...restProps
}: ICommonInput) => {
  const [inputValue, setInputValue] = useState(defaultValue || value);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(value)
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onChange(inputValue);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNoInput) {
      e.preventDefault();
      return false;
    }
    setInputValue(e.target.value);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Backspace' || e.keyCode === 8)) {
      onDelete();
    }
  };

  const handleFocus = () => {
    inputRef.current?.focus();
    setFocused(true);
    onFocus(inputValue);
  };

  const handleBlur = () => {
    inputRef.current?.blur();
    setFocused(false);
    onBlur(inputValue);
    onChange(inputValue);
  };

  const handleClear = () => {
    setInputValue('');
  };

  return (
    <div className={cx(Styles.root, className, {
      [Styles.focused]: focused,
      [Styles.disabled]: disabled,
      [Styles.error]: !!errorMsg,
      [Styles.rootPc]: !isMobile
    })}>
      <input
        ref={inputRef}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeydown}
        disabled={disabled}
        value={inputValue}
        maxLength={maxLength}
        {...restProps}
      />
      {
        !!inputValue && enableClear && <div className={Styles.clear} onClick={handleClear} />
      }
      {
        inputValue.length >= maxLength && <div className={Styles.error}>{errorMsg}</div>
      }
    </div>
  );
};

export default CommonInput;
